/**
 * Single source of truth for locales.
 * To add a locale later (de, fr, ru, tr, ar, vi): update this file only,
 * then add messages/<locale>.json. Set enabled: true only when translation is complete.
 */

export const locales = ["en", "es", "pt-BR", "hi", "id"] as const;

export type AppLocale = (typeof locales)[number];

export const defaultLocale: AppLocale = "en";

export type LocaleConfig = {
  /** BCP 47 locale code (also used for <html lang> and hreflang). */
  code: AppLocale;
  /** Native endonym shown in the language switcher. */
  nativeName: string;
  /**
   * When true, the locale appears in the language switcher, sitemap, and
   * hreflang, and may be indexed. Keep false until translations are complete
   * so unfinished locales never publish English copy as indexable.
   */
  enabled: boolean;
  dir: "ltr" | "rtl";
  /** Open Graph locale (underscore form). */
  ogLocale: string;
  /** Optional public URL prefix override (e.g. pt-BR → /pt). */
  prefix?: `/${string}`;
};

/**
 * One-file locale registry. Adding a language = one entry here + messages file.
 */
export const localeConfigs: Record<AppLocale, LocaleConfig> = {
  en: {
    code: "en",
    nativeName: "English",
    enabled: true,
    dir: "ltr",
    ogLocale: "en_US",
  },
  es: {
    code: "es",
    nativeName: "Español",
    enabled: true,
    dir: "ltr",
    ogLocale: "es_ES",
  },
  "pt-BR": {
    code: "pt-BR",
    nativeName: "Português",
    enabled: false,
    dir: "ltr",
    ogLocale: "pt_BR",
    prefix: "/pt",
  },
  hi: {
    code: "hi",
    nativeName: "हिन्दी",
    enabled: false,
    dir: "ltr",
    ogLocale: "hi_IN",
  },
  id: {
    code: "id",
    nativeName: "Bahasa Indonesia",
    enabled: false,
    dir: "ltr",
    ogLocale: "id_ID",
  },
};

/** Locales that may appear in the switcher, sitemap, and hreflang. */
export function getEnabledLocales(): AppLocale[] {
  return locales.filter((code) => localeConfigs[code].enabled);
}

/** @deprecated Prefer isLocaleEnabled — same gate for index/sitemap/hreflang/switcher. */
export function isLocaleIndexable(locale: string): boolean {
  return isLocaleEnabled(locale);
}

export function isLocaleEnabled(locale: string): boolean {
  return (
    (locales as readonly string[]).includes(locale) &&
    localeConfigs[locale as AppLocale].enabled
  );
}

export const localeDirections: Record<AppLocale, "ltr" | "rtl"> = {
  en: localeConfigs.en.dir,
  es: localeConfigs.es.dir,
  "pt-BR": localeConfigs["pt-BR"].dir,
  hi: localeConfigs.hi.dir,
  id: localeConfigs.id.dir,
};

export const localePrefixes: Partial<Record<AppLocale, `/${string}`>> = Object.fromEntries(
  locales
    .filter((code) => localeConfigs[code].prefix)
    .map((code) => [code, localeConfigs[code].prefix])
) as Partial<Record<AppLocale, `/${string}`>>;

export function getHtmlLang(locale: AppLocale): string {
  return locale;
}

export const ogLocales: Record<AppLocale, string> = {
  en: localeConfigs.en.ogLocale,
  es: localeConfigs.es.ogLocale,
  "pt-BR": localeConfigs["pt-BR"].ogLocale,
  hi: localeConfigs.hi.ogLocale,
  id: localeConfigs.id.ogLocale,
};

/** Keep for sitemap.ts compatibility during Commit 2 transition. */
export const indexableLocales = getEnabledLocales();
